import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LinkedInSidebar from './LinkedInSidebar';

const PortfolioAnalyser = ({ currentPage, setTitleDescription }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setTitleDescription("Fractofolio - Portfolio Analyzer", "The future of investment networking! Discover market insights, and make informed investment decisions with our community.")
  }, []);
  
  // Portfolio state
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [inputTicker, setInputTicker] = useState('');
  const [inputWeight, setInputWeight] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [weightType, setWeightType] = useState('percentage'); // 'percentage' or 'dollar'
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Portfolio metrics state
  const [portfolioMetrics, setPortfolioMetrics] = useState({
    weightedRisk: 0,
    overvaluedStocks: [],
    undervaluedStocks: [],
    fairlyValuedStocks: [],
    sectorAllocation: [],
    weightedSuccessRate: 0,
    totalValue: 0
  });

  // Calculate portfolio metrics when portfolio changes
  useEffect(() => {
    calculatePortfolioMetrics();
  }, [portfolioItems]);

  const calculatePortfolioMetrics = () => {
    if (portfolioItems.length === 0) {
      setPortfolioMetrics({
        weightedRisk: 0,
        overvaluedStocks: [],
        undervaluedStocks: [],
        fairlyValuedStocks: [],
        sectorAllocation: [],
        weightedSuccessRate: 0,
        totalValue: 0
      });
      return;
    }

    // Calculate total weight for normalization
    const totalWeight = portfolioItems.reduce((sum, item) => sum + item.weight, 0);
    
    // Normalize weights to 100%
    const normalizedItems = portfolioItems.map(item => ({
      ...item,
      normalizedWeight: (item.weight / totalWeight) * 100
    }));

    // Calculate weighted average risk (inverse of success rate)
    const weightedRisk = normalizedItems.reduce((sum, item) => {
      const risk = 100 - item.stock.successRate;
      return sum + (risk * item.normalizedWeight / 100);
    }, 0);

    // Calculate weighted success rate
    const weightedSuccessRate = normalizedItems.reduce((sum, item) => {
      return sum + (item.stock.successRate * item.normalizedWeight / 100);
    }, 0);

    // Categorize stocks by valuation (simplified logic based on PE ratio)
    const overvaluedStocks = [];
    const undervaluedStocks = [];
    const fairlyValuedStocks = [];

    normalizedItems.forEach(item => {
      const peRatio = item.stock.peRatio;
      const sectorAvgPE = getSectorAveragePE(item.stock.sector);
      
      if (peRatio > sectorAvgPE * 1.2) {
        overvaluedStocks.push({...item, valuationRatio: (peRatio / sectorAvgPE).toFixed(2)});
      } else if (peRatio < sectorAvgPE * 0.8) {
        undervaluedStocks.push({...item, valuationRatio: (peRatio / sectorAvgPE).toFixed(2)});
      } else {
        fairlyValuedStocks.push({...item, valuationRatio: (peRatio / sectorAvgPE).toFixed(2)});
      }
    });

    // Calculate sector allocation
    const sectorAllocation = Object.entries(
      normalizedItems.reduce((sectors, item) => {
        sectors[item.stock.sector] = (sectors[item.stock.sector] || 0) + item.normalizedWeight;
        return sectors;
      }, {})
    ).map(([sector, weight]) => ({
      sector,
      weight: parseFloat(weight.toFixed(2)),
      value: parseFloat(weight.toFixed(2))
    }));

    // Calculate total portfolio value
    const totalValue = portfolioItems.reduce((sum, item) => sum + item.dollarValue, 0);

    setPortfolioMetrics({
      weightedRisk: parseFloat(weightedRisk.toFixed(2)),
      overvaluedStocks,
      undervaluedStocks,
      fairlyValuedStocks,
      sectorAllocation,
      weightedSuccessRate: parseFloat(weightedSuccessRate.toFixed(2)),
      totalValue
    });
  };


  const addToPortfolio = (stock) => {
    // Check if stock is already in portfolio
    if (portfolioItems.some(item => item.stock.symbol === stock.symbol)) {
      alert('This stock is already in your portfolio!');
      return;
    }

    let weight = 0;
    let dollarValue = 0;

    if (weightType === 'percentage' && inputWeight) {
      weight = parseFloat(inputWeight);
      dollarValue = 0; // Will be calculated based on percentage
    } else if (weightType === 'dollar' && inputValue) {
      dollarValue = parseFloat(inputValue);
      weight = 1; // Will be normalized later
    } else {
      weight = 1; // Equal weight, will be normalized
      dollarValue = 0;
    }

    const newItem = {
      id: Date.now(),
      stock,
      weight,
      dollarValue,
      inputType: weightType
    };

    setPortfolioItems([...portfolioItems, newItem]);
    
    // Clear inputs
    setInputTicker('');
    setInputWeight('');
    setInputValue('');
    setShowSearchResults(false);
  };

  const removeFromPortfolio = (id) => {
    setPortfolioItems(portfolioItems.filter(item => item.id !== id));
  };

  const updatePortfolioItem = (id, field, value) => {
    setPortfolioItems(portfolioItems.map(item => 
      item.id === id 
        ? { ...item, [field]: parseFloat(value) || 0 }
        : item
    ));
  };

  // Color palette for charts
  const COLORS = ['#0077B5', '#4FC3F7', '#005885', '#57A0D3', '#2E8BC0', '#1A5F7A', '#82C8E6'];

  // Prepare valuation data for bar chart
  const valuationData = [
    { name: 'Undervalued', count: portfolioMetrics.undervaluedStocks.length, fill: '#057642' },
    { name: 'Fairly Valued', count: portfolioMetrics.fairlyValuedStocks.length, fill: '#F5C75D' },
    { name: 'Overvalued', count: portfolioMetrics.overvaluedStocks.length, fill: '#CC1016' }
  ];

  return (
    <div className="linkedin-layout bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="hidden lg:block lg:col-span-1">
            <LinkedInSidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </div>
          
          <div className="lg:col-span-3 space-y-6">
            {/* Page Header */}
            <div className="linkedin-card">
              <div className="linkedin-card-content">
                <div className="text-center mb-8">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Portfolio Analyser</h1>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Analyze your personal stock portfolio using Fractofolio's company valuation data. 
                    Get insights on risk, sector allocation, and identify undervalued opportunities.
                  </p>
                </div>
              </div>
            </div>

            {/* Add Stock to Portfolio */}
            <div className="linkedin-card">
              <div className="linkedin-card-header">
                <h3 className="text-lg font-bold text-gray-900">Add Stocks to Portfolio</h3>
              </div>
              <div className="linkedin-card-content">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter ticker or company name"
                      value={inputTicker}
                      onChange={(e) => setInputTicker(e.target.value)}
                      className="linkedin-input"
                    />
                    {showSearchResults && searchResults.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                        {searchResults.map((stock) => (
                          <div
                            key={stock.symbol}
                            onClick={() => {
                              setInputTicker(stock.symbol);
                              setShowSearchResults(false);
                            }}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-semibold text-gray-900">{stock.symbol}</div>
                            <div className="text-sm text-gray-600">{stock.name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <select
                      value={weightType}
                      onChange={(e) => setWeightType(e.target.value)}
                      className="linkedin-input"
                    >
                      <option value="percentage">% Weight</option>
                      <option value="dollar">$ Value</option>
                      <option value="equal">Equal Weight</option>
                    </select>
                    
                    {weightType === 'percentage' && (
                      <input
                        type="number"
                        placeholder="% (e.g., 12.5)"
                        value={inputWeight}
                        onChange={(e) => setInputWeight(e.target.value)}
                        className="linkedin-input"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    )}
                    
                    {weightType === 'dollar' && (
                      <input
                        type="number"
                        placeholder="$ (e.g., 3000)"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="linkedin-input"
                        min="0"
                        step="0.01"
                      />
                    )}
                  </div>
                  
                  <button
                    className="linkedin-btn-primary"
                    disabled={!inputTicker}
                  >
                    Add to Portfolio
                  </button>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p>💡 <strong>Tip:</strong> If you don't specify weights, all stocks will be equally weighted. You can adjust individual weights after adding stocks.</p>
                </div>
              </div>
            </div>

            {/* Current Portfolio */}
            {portfolioItems.length > 0 && (
              <div className="linkedin-card">
                <div className="linkedin-card-header">
                  <h3 className="text-lg font-bold text-gray-900">Your Portfolio ({portfolioItems.length} stocks)</h3>
                </div>
                <div className="linkedin-card-content">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-900">Company</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900">Price</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900">Weight %</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900">Value $</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolioItems.map((item) => (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-linkedin-blue bg-opacity-10 rounded-lg flex items-center justify-center">
                                  <span className="font-bold text-linkedin-blue text-sm">{item.stock.symbol}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">{item.stock.symbol}</p>
                                  <p className="text-sm text-gray-600">{item.stock.name}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className="font-medium text-gray-900">${item.stock.price}</span>
                            </td>
                            <td className="py-4 px-4">
                              <input
                                type="number"
                                value={item.weight}
                                onChange={(e) => updatePortfolioItem(item.id, 'weight', e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                min="0"
                                step="0.1"
                              />
                            </td>
                            <td className="py-4 px-4">
                              <input
                                type="number"
                                value={item.dollarValue}
                                onChange={(e) => updatePortfolioItem(item.id, 'dollarValue', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="py-4 px-4 text-center">
                              <button
                                onClick={() => removeFromPortfolio(item.id)}
                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                title="Remove from portfolio"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Portfolio Metrics */}
            {portfolioItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="linkedin-card">
                  <div className="linkedin-card-content text-center">
                    <div className="text-2xl font-bold text-linkedin-blue mb-2">
                      {portfolioMetrics.weightedRisk.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">Weighted Risk</div>
                  </div>
                </div>
                
                <div className="linkedin-card">
                  <div className="linkedin-card-content text-center">
                    <div className="text-2xl font-bold text-green-600 mb-2">
                      {portfolioMetrics.weightedSuccessRate.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                </div>
                
                <div className="linkedin-card">
                  <div className="linkedin-card-content text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      ${portfolioMetrics.totalValue.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Total Value</div>
                  </div>
                </div>
                
                <div className="linkedin-card">
                  <div className="linkedin-card-content text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {portfolioMetrics.sectorAllocation.length}
                    </div>
                    <div className="text-sm text-gray-600">Sectors</div>
                  </div>
                </div>
              </div>
            )}

            {/* Charts */}
            {portfolioItems.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sector Allocation Pie Chart */}
                <div className="linkedin-card">
                  <div className="linkedin-card-header">
                    <h3 className="text-lg font-bold text-gray-900">Sector Allocation</h3>
                  </div>
                  <div className="linkedin-card-content">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={portfolioMetrics.sectorAllocation}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({sector, weight}) => `${sector}: ${weight.toFixed(1)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {portfolioMetrics.sectorAllocation.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Valuation Status Bar Chart */}
                <div className="linkedin-card">
                  <div className="linkedin-card-header">
                    <h3 className="text-lg font-bold text-gray-900">Valuation Status</h3>
                  </div>
                  <div className="linkedin-card-content">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={valuationData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#0077B5" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Valuation Analysis */}
            {portfolioItems.length > 0 && (
              <div className="linkedin-card">
                <div className="linkedin-card-header">
                  <h3 className="text-lg font-bold text-gray-900">Valuation Analysis</h3>
                </div>
                <div className="linkedin-card-content">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Undervalued Stocks */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-3">
                        🟢 Undervalued ({portfolioMetrics.undervaluedStocks.length})
                      </h4>
                      {portfolioMetrics.undervaluedStocks.length > 0 ? (
                        <div className="space-y-2">
                          {portfolioMetrics.undervaluedStocks.map((item) => (
                            <div key={item.id} className="flex justify-between items-center">
                              <span className="text-sm font-medium text-green-800">{item.stock.symbol}</span>
                              <span className="text-xs text-green-600">{item.normalizedWeight.toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-green-600">No undervalued stocks</p>
                      )}
                    </div>

                    {/* Fairly Valued Stocks */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-800 mb-3">
                        🟡 Fairly Valued ({portfolioMetrics.fairlyValuedStocks.length})
                      </h4>
                      {portfolioMetrics.fairlyValuedStocks.length > 0 ? (
                        <div className="space-y-2">
                          {portfolioMetrics.fairlyValuedStocks.map((item) => (
                            <div key={item.id} className="flex justify-between items-center">
                              <span className="text-sm font-medium text-yellow-800">{item.stock.symbol}</span>
                              <span className="text-xs text-yellow-600">{item.normalizedWeight.toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-yellow-600">No fairly valued stocks</p>
                      )}
                    </div>

                    {/* Overvalued Stocks */}
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-red-800 mb-3">
                        🔴 Overvalued ({portfolioMetrics.overvaluedStocks.length})
                      </h4>
                      {portfolioMetrics.overvaluedStocks.length > 0 ? (
                        <div className="space-y-2">
                          {portfolioMetrics.overvaluedStocks.map((item) => (
                            <div key={item.id} className="flex justify-between items-center">
                              <span className="text-sm font-medium text-red-800">{item.stock.symbol}</span>
                              <span className="text-xs text-red-600">{item.normalizedWeight.toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-red-600">No overvalued stocks</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {portfolioItems.length === 0 && (
              <div className="linkedin-card">
                <div className="linkedin-card-content">
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2V7a2 2 0 012-2h2a2 2 0 002 2v2a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 00-2 2h-2a2 2 0 00-2 2v6a2 2 0 01-2 2H9z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Stocks in Portfolio</h3>
                    <p className="text-gray-600 mb-4">
                      Start building your portfolio by adding stocks above. 
                      You can search by ticker symbol or company name.
                    </p>
                    <button 
                      onClick={() => navigate('/search')}
                      className="linkedin-btn-primary"
                    >
                      Browse Companies
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioAnalyser;